import type { DefLookup } from "../catalog";
import { computeLengthSensitivity } from "../length-sensitivity";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import type { Issue, System } from "../types";

/** Self-clearing: `updateCable` drops `lengthEstimated` once a length is edited. */
export function detectAiEstimatedLengths(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const estimated = system.cables.filter((c) => c.lengthEstimated);
  if (estimated.length === 0) return [];

  const sensitivity = computeLengthSensitivity(system, defLookup, autoSizeTargetPct);
  const decisive = estimated.filter((c) => sensitivity.get(c.id)?.sensitive);
  if (decisive.length === 0) return [];

  const single = decisive.length === 1;
  return [
    {
      code: "ai-estimated-lengths",
      severity: "info",
      cableIds: decisive.map((c) => c.id),
      title: {
        key: single
          ? "diag.ai-estimated-lengths.title_one"
          : "diag.ai-estimated-lengths.title_many",
        vars: { count: decisive.length },
      },
      message: {
        key: single
          ? "diag.ai-estimated-lengths.message_one"
          : "diag.ai-estimated-lengths.message_many",
        vars: { count: decisive.length, total: estimated.length },
      },
      fix: {
        key: single
          ? "diag.ai-estimated-lengths.fix_one"
          : "diag.ai-estimated-lengths.fix_many",
      },
    },
  ];
}

/** Self-clearing: `replaceComponentDefinition` drops `partEstimated`. */
export function detectAiEstimatedParts(system: System): Issue[] {
  const nodeIds = system.nodes
    .filter((n) => n.kind === "component" && n.partEstimated)
    .map((n) => n.id);
  if (nodeIds.length === 0) return [];

  const single = nodeIds.length === 1;
  return [
    {
      code: "ai-estimated-parts",
      severity: "info",
      nodeIds,
      title: {
        key: single
          ? "diag.ai-estimated-parts.title_one"
          : "diag.ai-estimated-parts.title_many",
        vars: { count: nodeIds.length },
      },
      message: {
        key: single
          ? "diag.ai-estimated-parts.message_one"
          : "diag.ai-estimated-parts.message_many",
      },
      fix: {
        key: single
          ? "diag.ai-estimated-parts.fix_one"
          : "diag.ai-estimated-parts.fix_many",
      },
    },
  ];
}

export function runAiReviewDiagnostics(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  return [
    ...detectAiEstimatedLengths(system, defLookup, autoSizeTargetPct),
    ...detectAiEstimatedParts(system),
  ];
}
